import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import {
  Typography,
  Stack,
  Tabs,
  Tab,
  Box,
  IconButton,
} from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { getLongNumberStr } from '@/helpers/common';
import { formatLeaseLengthValue } from '@/helpers/leaseHelpers';
import {
  getComparableUnits,
  calculateGroupSummary,
  ComparableGroup,
  concessionsToMonths,
} from './comparablesUtils';
import { ComparableUnitTable, type ProposedMetrics as TableProposedMetrics } from './ComparableUnitTable';
import { TrendsSummary } from './TrendsSummary';
import { RootState } from '@/store';

interface ComparablesSectionProps {
  selectedUnit: Unit;
  allUnits: Unit[];
  leaseMap: Map<string, Lease>;
  allLeases?: Lease[];
  proposedMetrics?: TableProposedMetrics;
  currentMetrics?: {
    monthlyRent: number;
    leaseLength: number;
    concessions: number;
    effectiveMonthlyRent: number;
    annualRentPerSqft: number;
    effectiveAnnualRentPerSqft: number;
    leaseStartDate?: Date | null;
    leaseEndDate?: Date | null;
  };
  proposedDates?: {
    startDate: string;
    endDate: string;
  };
  selectedTabIndex?: number;
  onVisualizationGroupChange?: (unitIds: string[], groupName: string) => void;
  hoveredUnitId?: string | null;
  onHoverUnit?: (unitId: string | null) => void;
  showConcessionsAsMultiples?: boolean;
}

export function ComparablesSection({
  selectedUnit,
  allUnits,
  leaseMap,
  allLeases,
  proposedMetrics,
  currentMetrics,
  proposedDates,
  selectedTabIndex = 0,
  onVisualizationGroupChange,
  hoveredUnitId,
  onHoverUnit,
  showConcessionsAsMultiples = true,
}: ComparablesSectionProps) {
  // State for inner tabs (Before/After, Trends, Same Line, Same Bedrooms, Similar Sqft, Combined)
  const [innerTabIndex, setInnerTabIndex] = useState<number>(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Get visualizer from Redux (same one used for building visualization)
  const visualizer = useSelector((state: RootState) => state.project.projectVisualizer, shallowEqual);

  // Get comparable groups
  const comparables: ComparableGroup = getComparableUnits(selectedUnit, allUnits, leaseMap);

  const handleGroupVisualize = (unitIds: string[], groupName: string) => {
    onVisualizationGroupChange?.(unitIds, groupName);
  };

  // Calculate summaries for each group
  const sameLineSummary = calculateGroupSummary(comparables.sameLine, leaseMap);
  const sameBedroomsSummary = calculateGroupSummary(comparables.sameBedrooms, leaseMap);
  const similarSqftSummary = calculateGroupSummary(comparables.similarSqft, leaseMap);
  const combinedSummary = calculateGroupSummary(comparables.combined, leaseMap);

  // Get the current tab's data
  const tabGroups = useMemo(() => [
    { name: 'Same Line', units: comparables.sameLine, summary: sameLineSummary },
    { name: 'Same Bedrooms', units: comparables.sameBedrooms, summary: sameBedroomsSummary },
    { name: 'Similar Sqft', units: comparables.similarSqft, summary: similarSqftSummary },
    { name: 'Combined', units: comparables.combined, summary: combinedSummary },
  ], [comparables.sameLine, comparables.sameBedrooms, comparables.similarSqft, comparables.combined,
      sameLineSummary, sameBedroomsSummary, similarSqftSummary, combinedSummary]);

  // Map inner tab index to comparable group (tabs 0-1 are Before/After and Trends, tabs 2-5 are comparable groups)
  const selectedGroup = useMemo(() => {
    if (innerTabIndex < 2) return null;
    return tabGroups[innerTabIndex - 2];
  }, [tabGroups, innerTabIndex]);

  // Auto-visualize when tab changes
  useEffect(() => {
    if (selectedGroup && selectedGroup.units.length > 0) {
      handleGroupVisualize(selectedGroup.units.map((u) => u.id), selectedGroup.name);
    }
  }, [selectedTabIndex, selectedGroup, handleGroupVisualize]);

  // Check scroll position on mount and when tabs change
  useEffect(() => {
    const checkScroll = () => {
      if (tabsRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = 200;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });

      // Update button visibility after scroll
      setTimeout(() => {
        if (tabsRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
          setCanScrollLeft(scrollLeft > 0);
          setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
      }, 100);
    }
  };

  return (
    <div className="comparables-section">
      {/* Inner tabs for Before/After, Trends, and comparable groups */}
      <div className="flex items-center gap-2">
        <IconButton
          size="small"
          onClick={() => handleScroll('left')}
          disabled={!canScrollLeft}
          sx={{ flexShrink: 0 }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <div
          ref={tabsRef}
          style={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            scrollBehavior: 'smooth',
          }}
        >
          <Tabs
            value={innerTabIndex}
            onChange={(_, value) => setInnerTabIndex(value)}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              minWidth: 'min-content',
            }}
            variant="scrollable"
            scrollButtons={false}
          >
            <Tab label="Before/After" />
            <Tab label="Recent Leases" />
            <Tab label="Same Line" />
            <Tab label="Same Bedrooms" />
            <Tab label="Similar Sqft" />
            <Tab label="Combined" />
          </Tabs>
        </div>
        <IconButton
          size="small"
          onClick={() => handleScroll('right')}
          disabled={!canScrollRight}
          sx={{ flexShrink: 0 }}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>

      <Box sx={{ p: 2 }}>
        {/* Before/After Tab (Tab 0) */}
        {innerTabIndex === 0 && (
          <div>
            <Stack direction="row" spacing={2} alignItems="center" className="mb-4">
              <Typography variant="subtitle1">
                Before/After Comparison
              </Typography>
            </Stack>
            <Typography variant="body2" color="textSecondary" className="mb-4">
              Compare current lease terms with your proposed terms
            </Typography>
            {proposedMetrics && currentMetrics && (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-300 dark:border-zinc-600">
                    <th className="text-left py-2 px-3 font-bold text-zinc-100">Metric</th>
                    <th className="text-right py-2 px-3 font-bold text-zinc-100">Current</th>
                    <th className="text-right py-2 px-3 font-bold text-zinc-100">Proposed</th>
                    <th className="text-right py-2 px-3 font-bold text-zinc-100">Change</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-300 dark:border-zinc-600">
                    <td className="py-2 px-3 text-zinc-300">Monthly Rent</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{getLongNumberStr(currentMetrics.monthlyRent, 'currency')}</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{getLongNumberStr(proposedMetrics.monthlyRent, 'currency')}</td>
                    <td className="text-right py-2 px-3 font-semibold text-green-400">{getLongNumberStr(Math.round((proposedMetrics.monthlyRent - currentMetrics.monthlyRent) * 100) / 100, 'currency')}</td>
                  </tr>
                  <tr className="border-b border-zinc-300 dark:border-zinc-600">
                    <td className="py-2 px-3 text-zinc-300">Lease Length</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{formatLeaseLengthValue(currentMetrics.leaseLength)}</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{formatLeaseLengthValue(proposedMetrics.leaseLength)}</td>
                    <td className="text-right py-2 px-3 font-semibold text-green-400">{proposedMetrics.leaseLength - currentMetrics.leaseLength}</td>
                  </tr>
                  <tr className="border-b border-zinc-300 dark:border-zinc-600">
                    <td className="py-2 px-3 text-zinc-300">Concessions {showConcessionsAsMultiples ? '(mo)' : '($)'}</td>
                    <td className="text-right py-2 px-3 text-zinc-300">
                      {showConcessionsAsMultiples
                        ? `${concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent)} mo`
                        : getLongNumberStr(currentMetrics.concessions, 'currency')}
                    </td>
                    <td className="text-right py-2 px-3 text-zinc-300">
                      {showConcessionsAsMultiples
                        ? `${concessionsToMonths(proposedMetrics.concessions, proposedMetrics.monthlyRent)} mo`
                        : getLongNumberStr(proposedMetrics.concessions, 'currency')}
                    </td>
                    <td className={`text-right py-2 px-3 font-semibold ${(proposedMetrics.concessions - currentMetrics.concessions) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {showConcessionsAsMultiples
                        ? `${(concessionsToMonths(proposedMetrics.concessions, proposedMetrics.monthlyRent) - concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent)).toFixed(1)} mo`
                        : getLongNumberStr(Math.round((proposedMetrics.concessions - currentMetrics.concessions) * 100) / 100, 'currency')}
                    </td>
                  </tr>
                  <tr className="border-b border-zinc-300 dark:border-zinc-600">
                    <td className="py-2 px-3 font-bold text-zinc-100">Effective Monthly Rent</td>
                    <td className="text-right py-2 px-3 font-bold text-zinc-100">{getLongNumberStr(currentMetrics.effectiveMonthlyRent, 'currency')}</td>
                    <td className="text-right py-2 px-3 font-bold text-zinc-100">{getLongNumberStr(proposedMetrics.effectiveMonthlyRent, 'currency')}</td>
                    <td className={`text-right py-2 px-3 font-bold ${(proposedMetrics.effectiveMonthlyRent - currentMetrics.effectiveMonthlyRent) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{(proposedMetrics.effectiveMonthlyRent - currentMetrics.effectiveMonthlyRent) >= 0 ? '+' : ''}{getLongNumberStr(Math.round((proposedMetrics.effectiveMonthlyRent - currentMetrics.effectiveMonthlyRent) * 100) / 100, 'currency')}</td>
                  </tr>
                  <tr className="border-b border-zinc-300 dark:border-zinc-600">
                    <td className="py-2 px-3 text-zinc-300">Gross PSF</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{getLongNumberStr(currentMetrics.annualRentPerSqft, 'currency')}</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{getLongNumberStr(proposedMetrics.annualRentPerSqft, 'currency')}</td>
                    <td className="text-right py-2 px-3 font-semibold text-green-400">{getLongNumberStr(proposedMetrics.annualRentPerSqft - currentMetrics.annualRentPerSqft, 'currency')}</td>
                  </tr>
                  <tr className="border-b border-zinc-300 dark:border-zinc-600">
                    <td className="py-2 px-3 text-zinc-300">Net PSF</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{getLongNumberStr(currentMetrics.effectiveAnnualRentPerSqft, 'currency')}</td>
                    <td className="text-right py-2 px-3 text-zinc-300">{getLongNumberStr(proposedMetrics.effectiveAnnualRentPerSqft, 'currency')}</td>
                    <td className={`text-right py-2 px-3 font-semibold ${(proposedMetrics.effectiveAnnualRentPerSqft - currentMetrics.effectiveAnnualRentPerSqft) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{getLongNumberStr(proposedMetrics.effectiveAnnualRentPerSqft - currentMetrics.effectiveAnnualRentPerSqft,'currency')} </td>
                  </tr>
                  {(currentMetrics.leaseStartDate || proposedDates) && (
                    <>
                      <tr className="border-b border-zinc-300 dark:border-zinc-600">
                        <td className="py-2 px-3 text-zinc-300">Lease Start</td>
                        <td className="text-right py-2 px-3 text-zinc-300">{currentMetrics.leaseStartDate ? dayjs(currentMetrics.leaseStartDate).format('MM/DD/YYYY') : '-'}</td>
                        <td className="text-right py-2 px-3 text-zinc-300">{proposedDates ? dayjs(proposedDates.startDate).format('MM/DD/YYYY') : '-'}</td>
                        <td className="text-right py-2 px-3 text-zinc-400">-</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 text-zinc-300">Lease End</td>
                        <td className="text-right py-2 px-3 text-zinc-300">{currentMetrics.leaseEndDate ? dayjs(currentMetrics.leaseEndDate).format('MM/DD/YYYY') : '-'}</td>
                        <td className="text-right py-2 px-3 text-zinc-300">{proposedDates ? dayjs(proposedDates.endDate).format('MM/DD/YYYY') : '-'}</td>
                        <td className="text-right py-2 px-3 text-zinc-400">-</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Market Trends Tab (Tab 1) */}
        {innerTabIndex === 1 && allLeases && (
          <TrendsSummary allLeases={allLeases} allUnits={new Map(allUnits.map((u) => [u.id, u]))} />
        )}

        {/* Comparable Groups Tabs (Tabs 2-5) */}
        {innerTabIndex >= 2 && (
          <div className="space-y-4">
          
            {selectedGroup && selectedGroup.units.length > 0 ? (
              <ComparableUnitTable
                key={`${selectedTabIndex}-${selectedGroup.units.length}`}
                units={selectedGroup.units}
                leaseMap={leaseMap}
                summary={selectedGroup.summary}
                proposedMetrics={proposedMetrics}
                selectedUnitSqft={selectedUnit.sqft}
                visualizer={visualizer || undefined}
                selectedUnit={selectedUnit}
                showComparabilityReason={innerTabIndex === 5}
                hoveredUnitId={hoveredUnitId}
                onHoverUnit={onHoverUnit}
                showConcessionsAsMultiples={showConcessionsAsMultiples}
              />
            ) : (
              <Typography variant="body2" color="textSecondary" className="py-4">
                No units in this comparable group
              </Typography>
            )}
          </div>
        )}
      </Box>
    </div>
  );
}
